/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActionButton, ActionButtonType, CustomMenu, MenuItemButton, useMenuState } from "@octopusdeploy/design-system-components";
import type { MultiTenancyStatusResource, TenantResource, TenantSummary } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { bindActionCreators } from "redux";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { repository } from "~/clientInstance";
import { DropDownIcon } from "~/components/Button/DropDownIcon/DropDownIcon";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import PermissionCheck from "~/components/PermissionCheck/PermissionCheck";
import { Note, required, Select, Text } from "~/components/form";
import Callout, { CalloutType } from "~/primitiveComponents/dataDisplay/Callout/index";
import routeLinks from "~/routeLinks";
import { configurationActions } from "../../configuration/reducers/configurationArea";
export function AddOrCloneTenant({ type = ActionButtonType.Primary }: {
    type?: ActionButtonType;
}) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const { closeDialog: closeAddDialog, openDialog: openAddDialog, isOpen: isAddDialogOpen } = useDialogTrigger();
    const { closeDialog: closeCloneDialog, openDialog: openCloneDialog, isOpen: isCloneDialogOpen } = useDialogTrigger();
    const trackAction = useAnalyticActionDispatch();
    const navigation = useSpaceAwareNavigation();
    function openAddTenantDialog() {
        openAddDialog();
        trackAction("Open Add New Tenant Dialog", { resource: "Tenant", action: Action.View });
    }
    function openCloneTenantDialog() {
        openCloneDialog();
        trackAction("Open Clone Tenant Dialog", { resource: "Tenant", action: Action.View });
    }
    function onBlankTenantCreated(tenant: TenantResource) {
        closeAddDialog();
        trackAction("Add Blank Tenant", { resource: "Tenant", action: Action.Add });
        navigation.navigate(routeLinks.tenant(tenant.Id).root);
    }
    function onClonedTenantCreated(tenant: TenantResource) {
        closeCloneDialog();
        trackAction("Add Tenant From Clone", { resource: "Tenant", action: Action.Add });
        navigation.navigate(routeLinks.tenant(tenant.Id).root);
    }
    return (<PermissionCheck permission={Permission.TenantCreate}>
            <ActionButton type={type} icon={<DropDownIcon />} iconPosition="right" label="Add Tenant" onClick={openMenu} menuButtonAttributes={buttonAriaAttributes}/>
            <CustomMenu accessibleName={"Add Tenant"} menuState={menuState}>
                <MenuItemButton title={"Add blank tenant"} onClick={openAddTenantDialog}>
                    Add blank tenant
                </MenuItemButton>
                <MenuItemButton title={"Clone an existing tenant"} onClick={openCloneTenantDialog}>
                    Clone an existing tenant
                </MenuItemButton>
            </CustomMenu>
            <Dialog open={isAddDialogOpen}>
                <AddTenantDialog title="Add New Tenant" tenantCreated={onBlankTenantCreated}/>
            </Dialog>
            <Dialog open={isCloneDialogOpen}>
                <CloneTenantDialog title="Clone Tenant" tenantCreated={onClonedTenantCreated}/>
            </Dialog>
        </PermissionCheck>);
}
interface AddTenantDialogInternalProps {
    title: string;
    tenantCreated(tenant: TenantResource): any;
}
interface AddTenantDialogInternalState extends DataBaseComponentState {
    name: string;
    description?: string;
}
class AddTenantDialogInternal extends DataBaseComponent<GlobalConnectedProps & GlobalDispatchProps & AddTenantDialogInternalProps, AddTenantDialogInternalState> {
    constructor(props: GlobalConnectedProps & GlobalDispatchProps & AddTenantDialogInternalProps) {
        super(props);
        this.state = {
            name: "",
        };
    }
    save = async () => {
        return this.doBusyTask(async () => {
            const result = await createTenant(this.state.name, this.state.description);
            if (!this.props.isMultiTenancyEnabled) {
                const status = await repository.Tenants.status();
                this.props.onSpaceMultiTenancyStatusFetched(status);
            }
            this.props.tenantCreated(result);
            return true;
        });
    };
    render() {
        return (<SaveDialogLayout title={this.props.title} busy={this.state.busy} errors={this.errors} onSaveClick={this.save}>
                <Text label="New tenant name" value={this.state.name} onChange={(value) => this.setState({ name: value })} validate={required("Please enter a tenant name")} autoFocus={true}/>
                <Text label="Description" value={this.state.description || ""} onChange={(value) => this.setState({ description: value })}/>
            </SaveDialogLayout>);
    }
    static displayName = "AddTenantDialogInternal";
}
interface CloneTenantDialogInternalProps {
    title: string;
    tenantCreated(tenant: TenantResource): any;
}
interface CloneTenantDialogInternalState extends DataBaseComponentState {
    name: string;
    description?: string;
    cloneId?: string;
    tenantSummaries: TenantSummary[] | null;
}
class CloneTenantDialogInternal extends DataBaseComponent<GlobalConnectedProps & GlobalDispatchProps & CloneTenantDialogInternalProps, CloneTenantDialogInternalState> {
    constructor(props: GlobalConnectedProps & GlobalDispatchProps & CloneTenantDialogInternalProps) {
        super(props);
        this.state = {
            name: "",
            tenantSummaries: null,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const response = await repository.Tenants.tenantsSummaries();
            this.setState({ tenantSummaries: response.TenantSummaries });
        });
    }
    save = async () => {
        return this.doBusyTask(async () => {
            const result = await createTenant(this.state.name, this.state.description, this.state.cloneId);
            if (!this.props.isMultiTenancyEnabled) {
                const status = await repository.Tenants.status();
                this.props.onSpaceMultiTenancyStatusFetched(status);
            }
            this.props.tenantCreated(result);
            return true;
        });
    };
    render() {
        const items = this.state.tenantSummaries?.map((s) => ({ value: s.Id, text: s.Name })) ?? [];
        return (<SaveDialogLayout title={this.props.title} busy={this.state.busy} errors={this.errors} onSaveClick={this.save}>
                <Note>Configuration includes tenant tags, project and environment connections, and variable values.</Note>
                <Select items={items} value={this.state.cloneId} onChange={(value) => this.setState({ cloneId: value })} placeholder={"Select a tenant"} autoFocus={true}/>
                <Text label="New tenant name" value={this.state.name} onChange={(value) => this.setState({ name: value })} validate={required("Please enter a tenant name")}/>
                <Text label="Description" value={this.state.description || ""} onChange={(value) => this.setState({ description: value })}/>
            </SaveDialogLayout>);
    }
    static displayName = "CloneTenantDialogInternal";
}
interface CloneSpecificTenantDialogInternalProps {
    cloneId: string;
    cloneTenantName: string;
    tenantCreated(tenant: TenantResource): void;
}
interface CloneSpecificTenantDialogInternalState extends DataBaseComponentState {
    name: string;
    description?: string;
}
class CloneSpecificTenantDialogInternal extends DataBaseComponent<GlobalConnectedProps & GlobalDispatchProps & CloneSpecificTenantDialogInternalProps, CloneSpecificTenantDialogInternalState> {
    constructor(props: GlobalConnectedProps & GlobalDispatchProps & CloneSpecificTenantDialogInternalProps) {
        super(props);
        this.state = {
            name: "",
        };
    }
    save = async () => {
        return this.doBusyTask(async () => {
            const result = await createTenant(this.state.name, this.state.description, this.props.cloneId);
            if (!this.props.isMultiTenancyEnabled) {
                const status = await repository.Tenants.status();
                this.props.onSpaceMultiTenancyStatusFetched(status);
            }
            this.props.tenantCreated(result);
            return true;
        });
    };
    render() {
        return (<SaveDialogLayout title="Clone Tenant" busy={this.state.busy} errors={this.errors} onSaveClick={this.save}>
                <Text label="New tenant name" value={this.state.name} onChange={(value) => this.setState({ name: value })} validate={required("Please enter a tenant name")} autoFocus={true}/>
                <Text label="Description" value={this.state.description || ""} onChange={(value) => this.setState({ description: value })}/>
                <Callout type={CalloutType.Information} title="Scoping" key="clone-warning">
                    Targets, Accounts, Certificates, Permissions and other resources that are scoped to <strong>{this.props.cloneTenantName}</strong> will not be automatically updated to include this new Tenant.
                </Callout>
            </SaveDialogLayout>);
    }
    static displayName = "CloneSpecificTenantDialogInternal";
}
const createTenant = (name: string, description?: string, cloneId?: string) => {
    const args = { clone: cloneId };
    return repository.Tenants.create({
        Name: name,
        Description: description,
        TenantTags: [],
        ProjectEnvironments: {},
    }, args);
};
interface GlobalConnectedProps {
    isMultiTenancyEnabled: boolean;
}
const mapGlobalStateToProps = (state: GlobalState): GlobalConnectedProps => {
    return {
        isMultiTenancyEnabled: state.configurationArea.currentSpace.isMultiTenancyEnabled,
    };
};
interface GlobalDispatchProps {
    onSpaceMultiTenancyStatusFetched: (status: MultiTenancyStatusResource) => void;
}
const mapGlobalActionDispatchersToProps = (dispatch: Dispatch): GlobalDispatchProps => bindActionCreators({ onSpaceMultiTenancyStatusFetched: configurationActions.spaceMultiTenancyStatusFetched }, dispatch);
export const AddTenantDialog = connect(mapGlobalStateToProps, mapGlobalActionDispatchersToProps)(AddTenantDialogInternal);
const CloneTenantDialog = connect(mapGlobalStateToProps, mapGlobalActionDispatchersToProps)(CloneTenantDialogInternal);
export const CloneSpecificTenantDialog = connect(mapGlobalStateToProps, mapGlobalActionDispatchersToProps)(CloneSpecificTenantDialogInternal);
